import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { AnimatePresence } from 'framer-motion';
import { Popover } from '@mui/material';
import { ChipSummary } from '@/components/Approval/RightContainer/FilterBar/chipSummary';
import {
  Chevron,
  ChipButton,
  ChipDivider,
  ChipExtra,
  ChipLabel,
  ChipValue,
  ChipValues,
  popoverPaperSx,
} from '@/components/Approval/RightContainer/FilterBar/styles';
import {
  SummarySlot,
  useChipMotion,
} from '@/components/Approval/RightContainer/FilterBar/chipMotion';

type Props = {
  /** 칩에 항상 표시되는 필터명 (예: 양식, 결재자) */
  label: string;
  /** 선택값 요약("첫 항목 +N"). null 이면 미선택 상태 */
  summary?: ChipSummary;
  /** 팝오버 본문(필터 편집 패널) */
  children: ReactNode;
  /** 팝오버 폭(px). 검색이 있는 필터는 넓게 */
  popoverWidth?: number;
  /** 필터 더보기로 갓 추가된 칩이면 마운트되자마자 팝오버를 연다 */
  autoOpen?: boolean;
  /** autoOpen 처리 후 신호 초기화 */
  onAutoOpened?: () => void;
  /** 값이 있을 때 칩에 X(지우기) 노출 — 클릭 시 이 필터만 해제 */
  onClear?: () => void;
};

/**
 * 필터바의 범용 칩 팝오버 셸.
 * - 칩 트리거: 필터명 + (선택 시) 선택값 인라인 요약 + chevron
 * - 클릭 시 아래로 Popover 를 열어 편집 패널(children)을 호스팅
 * 편집은 전역 approvalFilter atom 을 직접 갱신하므로, 선택값 요약(summary)은
 * 부모가 atom 을 구독해 실시간으로 넘겨준다.
 */
const FilterChipPopover = ({
  label,
  summary,
  children,
  popoverWidth = 320,
  autoOpen,
  onAutoOpened,
  onClear,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);
  const hasValue = !!summary;
  const { slotMotion } = useChipMotion();

  useEffect(() => {
    if (autoOpen && buttonRef.current) {
      setAnchorEl(buttonRef.current);
      onAutoOpened?.();
    }
    // autoOpen 이 true 로 바뀌는 순간에만
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  return (
    <>
      <ChipButton
        ref={buttonRef}
        type="button"
        selected={hasValue}
        open={open}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <ChipLabel>{label}</ChipLabel>
        {/* 선택값이 붙고 빠질 때 칩 너비가 튀지 않도록 폭·투명도를 함께 전환한다 */}
        <AnimatePresence initial={false}>
          {summary && (
            <SummarySlot key={'summary'} {...slotMotion}>
              <ChipDivider />
              <ChipValues>
                <ChipValue>
                  {summary.label}
                  {onClear && (
                    <ClearChip
                      role="button"
                      aria-label="clear"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClear();
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M12 4L4 12M4 4L12 12"
                          stroke="#888D96"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </ClearChip>
                  )}
                </ChipValue>
                {summary.extraCount > 0 && (
                  <ChipExtra>{`+${summary.extraCount}`}</ChipExtra>
                )}
              </ChipValues>
            </SummarySlot>
          )}
        </AnimatePresence>
        <Chevron open={open} aria-hidden>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M5.833 8L10 12.167L14.167 8"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Chevron>
      </ChipButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { ...popoverPaperSx, width: `${popoverWidth}px` } }}
      >
        {children}
      </Popover>
    </>
  );
};

export default FilterChipPopover;

/** 칩 안 X(지우기) — 버튼 중첩 방지를 위해 span, 클릭은 전파 차단 */
const ClearChip = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;
